import { faCaretDown } from "@fortawesome/pro-solid-svg-icons/faCaretDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Combobox, type ComboboxStore, Loader, TextInput } from "@mantine/core";
import { useFocusWithin, usePrevious } from "@mantine/hooks";
import { useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { stopPropagationWrapper } from "@/utils/ui";

interface AssignUserSingleSelectInputProps {
  combobox: ComboboxStore;
  fullWidth: boolean;
  loading: boolean;
  onBlur: () => void;
  search: string;
  setSearch: (search: string) => void;
  testId: string;
}

export const AssignUserSingleSelectInput = ({
  combobox,
  fullWidth,
  loading,
  onBlur,
  search,
  setSearch,
  testId,
}: AssignUserSingleSelectInputProps) => {
  const dropdownWasOpen = usePrevious(combobox.dropdownOpened);
  const { ref, focused } = useFocusWithin<HTMLInputElement>();

  // The default behavior on selecting an option returns focus to the input on
  // select. In this case, we assume the user is finished with the input once
  // they have selected their option.
  useEffect(() => {
    if (!combobox.dropdownOpened && dropdownWasOpen && focused) {
      ref.current?.blur();
    }
  }, [combobox.dropdownOpened, dropdownWasOpen, focused, ref]);

  return (
    <Combobox.Target>
      <TextInput
        classNames={{
          input: twMerge([
            "bg-uw-gray-1",
            "border-none",
            "text-uw-gray-7",
            "font-bold",
            "h-5",
            "overflow-hidden",
            "placeholder:text-uw-gray-7",
            "pl-2",
            "py-0.5",
            "rounded-full",
            "text-ellipsis",
            "text-uw-gray-7",
            "text-xs",
            "uppercase",
          ]),
          wrapper: fullWidth ? "" : "w-32 ",
        }}
        data-testid={testId}
        disabled={loading}
        leftSection={loading && <Loader size="xs" />}
        onBlur={onBlur}
        onChange={(e) => setSearch(e.target.value)}
        // When used in the assign user cell, click events propagate up to the
        // row and trigger navigation
        onClick={stopPropagationWrapper()}
        onFocus={() => {
          combobox.openDropdown();

          // Clear search on focus to show the full list of available users.
          // Resets to actual value in the blur handler
          setSearch("");
        }}
        placeholder="Select..."
        ref={ref}
        rightSection={focused ? null : <FontAwesomeIcon icon={faCaretDown} />}
        rightSectionPointerEvents="none"
        size="xs"
        value={search}
      />
    </Combobox.Target>
  );
};
